import { getSpawnPos, isWorldPosInTunnel, resolvePlayablePos, movePosOnWalkable } from "../systems/level.js";
import { isLoaded, getSavedPlayerPos, getCheckpointPos, saveMemorySystem, unloadRegionLockedModules } from "../systems/memory.js";
import { isMemoryUIOpen, isMouseOverMemoryPanel, isMemoryTutorialLocked } from "../ui/memoryUI.js";
import { isEscapeUIOpen } from "../ui/escapeUI.js";
import { isTextboxAdvanceInputConsumed, isTextboxOpen } from "../ui/textboxUI.js";
import { checkRoomNarration, checkAdSignNarration, queueBootNarration } from "../systems/roomNarration.js";
import { initVision, updateVision } from "../systems/vision.js";
import { GAME_CONFIG } from "../config/gameConfig.js";
import { attractClosestHumanTo, triggerHumanAlarm } from "./humanAI.js";
import { triggerSniff, updateSniffMarks } from "../systems/sniff.js";
import { setLight, removeLight } from "../systems/lighting.js";
import { ensureCrosshairOverlay, hideCrosshairOverlay, removeCrosshairOverlay, showCrosshairAt } from "../ui/crosshair.js";
import { getRoomAt, getNearestRoom } from "../systems/rooms.js";
import { isAlarmRoomLocked } from "../systems/alarmLockdown.js";
import { isGameplayPaused } from "../systems/pauseState.js";
import { AUDIO_SOUNDS, playBarkLocal, playLocal, playUi, setAudioListener, setAudioWorldPaused } from "../systems/audio.js";

/**
 * Player Entity — Android Dog with RAM-Dependent Abilities
 */

const DOG_VISUAL_SCALE = 1.2675;
const DOG_VISUAL_Y_OFFSET = -15;
const WALK_DUST_INTERVAL = 0.08;
const WALK_DUST_Z = 9701;
const PAW_PRINT_INTERVAL = 0.22;
const PAW_PRINT_Z = 9701;
const SHAKE_HURT = 3.5;
const SHAKE_BITE = 1.2;
const SHAKE_BARK = 2.4;
const DOG_SHADOW_Z = 9702;
const DOG_BODY_Z = 9705;
const DOG_HELPER_Z = 9706;
const DOG_EFFECT_Z = 9707;
const DOG_EYE_OUTER_Z = 9708;
const DOG_EYE_CORE_Z = 9709;
const DOG_EYE_OFFSET_X = 8;
const DOG_EYE_OFFSET_Y = -7;
const DOG_EYE_LIGHT_ID = "dog_eye_glow";
const DOG_EYE_BLINK_INTERVAL = 2.6;
const DOG_EYE_BLINK_VARIANCE = 1.1;
const DOG_EYE_BLINK_DURATION = 0.08;
const DOG_EYE_ACTION_GLOW_SECONDS = 0.28;
const DOG_EYE_TRAIL_INTERVAL = 0.025;
const TRAP_VISUAL_SCALE = 0.12;
const TRAP_VISUAL_Y_OFFSET = 10;
const PLAYER_MOVE_STEP_MAX = 4;
const PLAYER_BLOCKER_PADDING = 8;
const DYNAMIC_BLOCKER_TAGS = ["locker_door_block", "cage_door"];

function getDogGlowConfig() {
    return GAME_CONFIG.lighting?.dogGlow || {};
}

const DOG_ANIMS = {
    idle: { sprite: "dog_idle", anim: "idle" },
    walk: { sprite: "dog_walk", anim: "walk" },
    attack: { sprite: "dog_attack", anim: "attack" },
    hurt: { sprite: "dog_hurt", anim: "hurt" },
    death: { sprite: "dog_death", anim: "death" },
};

function spawnWalkDust(player, moveDir) {
    if (!player || !moveDir || moveDir.len() <= 0) return;

    const backDir = moveDir.scale(-1);
    const sideDir = vec2(-backDir.y, backDir.x);
    const jitter = (Math.random() - 0.5) * 8;
    const footPos = player.pos
        .add(vec2(-3, 14))
        .add(backDir.scale(5))
        .add(sideDir.scale(jitter));

    const dust = add([
        circle(2 + Math.random() * 1.6),
        pos(footPos),
        anchor("center"),
        scale(1),
        color(96, 88, 78),
        opacity(0.36),
        z(WALK_DUST_Z),
        "walk_dust",
        {
            age: 0,
            lifetime: 0.3 + Math.random() * 0.14,
            velocity: backDir.scale(8 + Math.random() * 11).add(sideDir.scale(jitter * 0.6)),
        },
    ]);

    dust.onUpdate(() => {
        dust.age += dt();
        const t = Math.min(1, dust.age / dust.lifetime);
        dust.pos = dust.pos.add(dust.velocity.scale(dt()));
        dust.opacity = (1 - t) * 0.36;
        dust.scale = vec2(1 + t * 0.75);
        if (t >= 1) destroy(dust);
    });
}

function spawnPawPrint(player, moveDir) {
    if (!player || !moveDir || moveDir.len() <= 0) return;

    const forwardDir = moveDir.unit();
    const sideDir = vec2(-forwardDir.y, forwardDir.x);
    const sideOffset = player.pawStep % 2 === 0 ? -4 : 4;
    const basePos = player.pos
        .add(vec2(-3, 14))
        .add(forwardDir.scale(-5))
        .add(sideDir.scale(sideOffset));

    const parts = [
        { offset: vec2(0, 0), size: 2.2, alpha: 0.34 },
        { offset: forwardDir.scale(3).add(sideDir.scale(-2)), size: 1.15, alpha: 0.3 },
        { offset: forwardDir.scale(4), size: 1.2, alpha: 0.32 },
        { offset: forwardDir.scale(3).add(sideDir.scale(2)), size: 1.15, alpha: 0.3 },
    ];

    for (const part of parts) {
        const mark = add([
            circle(part.size),
            pos(basePos.add(part.offset)),
            anchor("center"),
            color(42, 36, 30),
            opacity(part.alpha),
            z(PAW_PRINT_Z),
            "paw_print",
            {
                age: 0,
                lifetime: 1.25,
                startOpacity: part.alpha,
            },
        ]);

        mark.onUpdate(() => {
            mark.age += dt();
            const t = Math.min(1, mark.age / mark.lifetime);
            mark.opacity = mark.startOpacity * (1 - t);
            if (t >= 1) destroy(mark);
        });
    }
}

function spawnBarkSoundWave(origin, maxRadius, duration) {
    for (let ring = 0; ring < 3; ring++) {
        const ringDelay = ring * 0.055;
        const segmentCount = 12 + ring * 4;
        const segmentW = 7 + ring * 2;
        const ringScale = 0.38 + ring * 0.27;

        wait(ringDelay, () => {
            for (let i = 0; i < segmentCount; i++) {
                const angle = (360 / segmentCount) * i;
                const dir = Vec2.fromAngle(angle);
                const wave = add([
                    rect(segmentW, 3),
                    pos(origin.add(dir.scale(10 + ring * 5))),
                    anchor("center"),
                    rotate(angle + 90),
                    color(180, 190, 205),
                    opacity(0.75),
                    z(9600),
                    "bark_sound_wave",
                    {
                        elapsed: 0,
                        duration,
                        dir,
                        startDist: 10 + ring * 5,
                        maxDist: maxRadius * ringScale,
                    },
                ]);

                wave.onUpdate(() => {
                    wave.elapsed += dt();
                    const t = Math.min(1, wave.elapsed / wave.duration);
                    const dist = lerp(wave.startDist, wave.maxDist, t);
                    wave.pos = origin.add(wave.dir.scale(dist));
                    wave.opacity = (1 - t) * 0.75;
                    wave.scale = vec2(1 + t * 0.25, 1);
                    if (t >= 1) destroy(wave);
                });
            }
        });
    }
}

function pointDistToSegment(point, start, end) {
    const segment = end.sub(start);
    const segmentLenSq = segment.x * segment.x + segment.y * segment.y;
    if (segmentLenSq <= 0.001) return point.dist(start);

    const toPoint = point.sub(start);
    const t = Math.max(0, Math.min(1, (toPoint.x * segment.x + toPoint.y * segment.y) / segmentLenSq));
    return point.dist(start.add(segment.scale(t)));
}

function isPointInExpandedRect(point, topLeft, widthValue, heightValue, padding = 0) {
    return point.x >= topLeft.x - padding
        && point.x <= topLeft.x + widthValue + padding
        && point.y >= topLeft.y - padding
        && point.y <= topLeft.y + heightValue + padding;
}

function isBlockedByDynamicSolid(worldPos) {
    const tileW = GAME_CONFIG.level.tileWidth;
    const tileH = GAME_CONFIG.level.tileHeight;

    for (const tag of DYNAMIC_BLOCKER_TAGS) {
        for (const blocker of get(tag, { recursive: true })) {
            if (!blocker?.exists?.()) continue;
            const blockW = typeof blocker.width === "number" ? blocker.width : tileW;
            const blockH = typeof blocker.height === "number" ? blocker.height : tileH;
            if (isPointInExpandedRect(worldPos, blocker.pos, blockW, blockH, PLAYER_BLOCKER_PADDING)) {
                return true;
            }
        }
    }

    return false;
}

function movePlayerWithinAlarmLock(player, velocity) {
    const delta = velocity?.scale ? velocity.scale(dt()) : vec2(0, 0);
    const steps = Math.max(1, Math.ceil(delta.len() / PLAYER_MOVE_STEP_MAX));
    const stepDelta = delta.scale(1 / steps);

    for (let i = 0; i < steps; i++) {
        const before = vec2(player.pos.x, player.pos.y);
        const beforeRoom = getRoomAt(before);
        const slid = movePosOnWalkable(before, stepDelta);

        if (isBlockedByDynamicSolid(slid.pos)) {
            player.pos = before;
            return { moved: i > 0, hitWall: true };
        }

        player.pos = slid.pos;

        if (beforeRoom && isAlarmRoomLocked(beforeRoom.id)) {
            const afterRoom = getRoomAt(player.pos);
            if (afterRoom?.id !== beforeRoom.id) {
                player.pos = before;
                return { moved: i > 0, hitWall: true };
            }
        }

        if (slid.hitWall) return { moved: i > 0 || slid.moved, hitWall: true };
    }

    return { moved: delta.len() > 0, hitWall: false };
}

function setDogAnimation(player, state) {
    const dogVisual = player.dogVisual;
    if (!dogVisual) return;

    const dogShadow = player.shadow;
    const next = DOG_ANIMS[state] || DOG_ANIMS.idle;
    const facingLeft = player.facing < 0;

    if (dogVisual.sprite !== next.sprite) {
        dogVisual.use(sprite(next.sprite, {
            anim: next.anim,
            flipX: facingLeft,
        }));
        dogVisual.flipX = facingLeft;

        if (dogShadow?.exists?.() !== false) {
            dogShadow.use(sprite(next.sprite, {
                anim: next.anim,
                flipX: facingLeft,
                flipY: true,
            }));
            dogShadow.flipX = facingLeft;
            dogShadow.flipY = true;
        }
        if (player.animsFrozen) freezeDogAnims(player, true);
        return;
    }

    dogVisual.flipX = facingLeft;
    if (dogShadow?.exists?.() !== false) {
        dogShadow.flipX = facingLeft;
        dogShadow.flipY = true;
    }

    if (dogVisual.getCurAnim()?.name !== next.anim) {
        dogVisual.play(next.anim);
    }
    if (dogShadow?.exists?.() !== false && dogShadow.getCurAnim()?.name !== next.anim) {
        dogShadow.play(next.anim);
    }
    if (player.animsFrozen) freezeDogAnims(player, true);
}

function freezeDogAnims(player, frozen) {
    player.animsFrozen = !!frozen;
    const speed = frozen ? 0 : 1;
    if (player.dogVisual?.exists?.()) player.dogVisual.animSpeed = speed;
    if (player.shadow?.exists?.()) player.shadow.animSpeed = speed;
}

function updateDogEyeGlow(player) {
    if (!player.eyeGlowOuter?.exists?.() || !player.eyeGlowCore?.exists?.()) return;

    const actionGlow = player.eyeActionTimer > 0;

    player.eyePulse += dt();
    if (player.eyeBlinkHold > 0) {
        player.eyeBlinkHold -= dt();
    } else {
        player.eyeBlinkTimer -= dt();
        if (player.eyeBlinkTimer <= 0) {
            player.eyeBlinkHold = DOG_EYE_BLINK_DURATION;
            player.eyeBlinkTimer = DOG_EYE_BLINK_INTERVAL + Math.random() * DOG_EYE_BLINK_VARIANCE;
        }
    }

    const pulse = 0.5 + Math.sin(player.eyePulse * 8) * 0.5;
    const blinkOpacity = actionGlow ? 1 : (player.eyeBlinkHold > 0 ? 0 : 1);
    const eyeOffset = vec2(
        player.facing < 0 ? -DOG_EYE_OFFSET_X : DOG_EYE_OFFSET_X,
        DOG_EYE_OFFSET_Y,
    );
    const eyeOpacity = (player.isDigging ? 0.55 : 1) * blinkOpacity;
    const glow = getDogGlowConfig();
    const eyeColor = actionGlow
        ? (glow.actionColor || [255, 35, 35])
        : (glow.color || [70, 190, 255]);

    player.eyeGlowOuter.pos = eyeOffset;
    player.eyeGlowOuter.color = rgb(eyeColor[0], eyeColor[1], eyeColor[2]);
    player.eyeGlowOuter.opacity = (actionGlow ? 0.4 : 0.22 + pulse * 0.14) * eyeOpacity;
    player.eyeGlowOuter.scale = vec2(actionGlow ? 1.15 : 1.0 + pulse * 0.2);

    player.eyeGlowCore.pos = eyeOffset;
    player.eyeGlowCore.color = rgb(eyeColor[0], eyeColor[1], eyeColor[2]);
    player.eyeGlowCore.opacity = (actionGlow ? 0.85 : 0.8 + pulse * 0.15) * eyeOpacity;

    if (actionGlow) {
        player.eyeTrailTimer -= dt();
        if (player.eyeTrailTimer <= 0) {
            player.eyeTrailTimer = DOG_EYE_TRAIL_INTERVAL;
            spawnDogEyeTrail(player.pos.add(eyeOffset));
        }
    }

    const eyeRoom = getRoomAt(player.pos) || getNearestRoom(player.pos);
    const baseRadius = actionGlow
        ? (glow.actionRadius ?? 42)
        : (glow.radius ?? 78);
    const baseIntensity = actionGlow
        ? (glow.actionIntensity ?? 0.55)
        : (glow.intensity ?? 0.72);

    setLight(DOG_EYE_LIGHT_ID, {
        pos: player.pos.add(eyeOffset),
        radius: actionGlow ? baseRadius : baseRadius + pulse * 10,
        color: eyeColor,
        intensity: baseIntensity * blinkOpacity,
        pulse: false,
        roomId: eyeRoom ? eyeRoom.id : null,
    });
}

function spawnDogEyeTrail(worldPos) {
    const trail = add([
        circle(3),
        pos(worldPos),
        anchor("center"),
        color(255, 30, 30),
        opacity(0.38),
        z(DOG_EFFECT_Z),
        {
            age: 0,
            lifetime: 0.2,
        },
    ]);

    trail.onUpdate(() => {
        trail.age += dt();
        const t = Math.min(1, trail.age / trail.lifetime);
        trail.opacity = (1 - t) * 0.38;
        trail.scale = vec2(0.8 + t * 1.1);
        if (t >= 1) destroy(trail);
    });
}

export function initPlayer(opts = {}) {
    destroyAll("player");
    destroyAll("player_shadow");

    const checkpoint = getCheckpointPos();
    const savedPos = getSavedPlayerPos();
    const defaultSpawn = getSpawnPos();

    // Death/reboot always uses checkpoint (or map spawn) — never last walk-save position
    let targetPos;
    if (opts.respawnFromCheckpoint) {
        targetPos = checkpoint ? vec2(checkpoint.x, checkpoint.y) : defaultSpawn;
    } else {
        targetPos = checkpoint ? vec2(checkpoint.x, checkpoint.y) : (savedPos ? vec2(savedPos.x, savedPos.y) : defaultSpawn);
    }

    // Old saves / tiny test-map coords often land off the SpriteFusion floor
    targetPos = resolvePlayablePos(targetPos);

    const cfg = GAME_CONFIG.player;

    const player = add([
        rect(14, 14),
        color(255, 255, 255),
        opacity(0),
        pos(targetPos),
        anchor("center"),
        area({ shape: new Rect(vec2(0), 14, 14) }),
        body({ isStatic: false }),
        health(cfg.maxHp),
        z(DOG_BODY_Z),
        "player",
        {
            baseSpeed: cfg.baseSpeed,
            aimAngle: 0,
            isDigging: false,
            isBarking: false,
            trapLock: 0,
            biteCooldown: 0,
            biteInvulnerability: 0,
            barkCooldown: 0,
            sniffCooldown: 0,
            dustTimer: 0,
            pawTimer: 0,
            pawStep: 0,
            lungeVelocity: vec2(0, 0),
            facing: 1,
            eyePulse: 0,
            eyeBlinkTimer: DOG_EYE_BLINK_INTERVAL,
            eyeBlinkHold: 0,
            eyeActionTimer: 0,
            eyeTrailTimer: 0,
            animationLock: 0,
            isDead: false,
        },
    ]);

    player.shadow = player.add([
        sprite(DOG_ANIMS.idle.sprite, {
            anim: DOG_ANIMS.idle.anim,
            flipY: true,
        }),
        pos(-3, 18),
        anchor("center"),
        scale(DOG_VISUAL_SCALE, DOG_VISUAL_SCALE * 0.55),
        color(0, 0, 0),
        opacity(0.28),
        z(DOG_SHADOW_Z),
        "player_shadow",
    ]);

    player.dogVisual = player.add([
        sprite(DOG_ANIMS.idle.sprite, { anim: DOG_ANIMS.idle.anim }),
        scale(DOG_VISUAL_SCALE),
        color(255, 255, 255),
        anchor("center"),
        pos(0, DOG_VISUAL_Y_OFFSET),
        z(DOG_BODY_Z),
    ]);

    const glowColor = getDogGlowConfig().color || [70, 190, 255];

    player.eyeGlowOuter = player.add([
        circle(6),
        pos(DOG_EYE_OFFSET_X, DOG_EYE_OFFSET_Y),
        anchor("center"),
        color(glowColor[0], glowColor[1], glowColor[2]),
        opacity(0.35),
        z(DOG_EYE_OUTER_Z),
    ]);

    player.eyeGlowCore = player.add([
        circle(2.2),
        pos(DOG_EYE_OFFSET_X, DOG_EYE_OFFSET_Y),
        anchor("center"),
        color(glowColor[0], glowColor[1], glowColor[2]),
        opacity(0.95),
        z(DOG_EYE_CORE_Z),
    ]);

    player.trapVisual = player.add([
        sprite("trap"),
        pos(0, TRAP_VISUAL_Y_OFFSET),
        anchor("center"),
        scale(TRAP_VISUAL_SCALE),
        opacity(0),
        z(DOG_EYE_CORE_Z),
    ]);

    setCamPos(targetPos);

    const snout = player.add([
        rect(4, 4),
        color(0, 255, 180),
        anchor("center"),
        pos(7, 0),
        opacity(0),
        z(DOG_HELPER_Z),
    ]);

    const burrowIcon = player.add([
        rect(10, 10),
        color(80, 50, 20),
        anchor("center"),
        pos(0, 0),
        opacity(0),
        z(DOG_SHADOW_Z),
    ]);

    const sniffIcon = player.add([
        circle(5),
        color(80, 255, 160),
        anchor("center"),
        pos(0, -12),
        opacity(0),
        z(DOG_EFFECT_Z),
    ]);

    initVision();

    const canvas = document.querySelector("canvas");
    removeCrosshairOverlay();
    ensureCrosshairOverlay();
    const crosshair = add([
        pos(0, 0),
        anchor("center"),
        fixed(),
        z(9999),
    ]);
    crosshair.add([
        rect(12, 2),
        anchor("center"),
        color(0, 255, 80),
        opacity(0.9),
    ]);
    crosshair.add([
        rect(2, 12),
        anchor("center"),
        color(0, 255, 80),
        opacity(0.9),
    ]);

    const playHurtFeedback = () => {
        shake(SHAKE_HURT);
        player.animationLock = 0.18;
        setDogAnimation(player, "hurt");
        wait(0.12, () => {
            if (player.exists()) {
                player.dogVisual.color = rgb(255, 255, 255);
            }
        });
    };

    player.onHurt(playHurtFeedback);

    player.onDeath(() => {
        player.isDead = true;
        playUi(AUDIO_SOUNDS.playerDeath);
        removeCrosshairOverlay();
        removeLight(DOG_EYE_LIGHT_ID);
        player.animationLock = 0.5;
        setDogAnimation(player, "death");
        // Hold on the death beat before the crash UI — anim is short, linger after.
        wait(2.2, () => {
            go("crash", { reason: "damage" });
        });
    });

    player.takeDamage = (dmg) => {
        if (player.biteInvulnerability > 0) return false;
        player.hp -= dmg;
        // Custom HP path may not fire KAPLAY onHurt — keep hurt SFX here.
        playLocal(AUDIO_SOUNDS.playerHurt);
        return true;
    };

    player.applyKnockback = (dirVec, force) => {
        if (player.trapLock > 0) return;
        player.lungeVelocity = dirVec.scale(force);
    };

    player.applyTrap = (duration = 0.5, damage = 0) => {
        player.trapLock = Math.max(player.trapLock || 0, duration);
        player.lungeVelocity = vec2(0, 0);
        player.isDigging = false;
        player.isBarking = false;
        if (damage > 0) {
            player.hp -= damage;
            playHurtFeedback();
        } else {
            player.animationLock = Math.max(player.animationLock, 0.18);
            setDogAnimation(player, "hurt");
        }
        if (player.dogVisual?.exists?.()) {
            player.dogVisual.opacity = 1;
        }
        if (burrowIcon?.exists?.()) {
            burrowIcon.opacity = 0;
        }
        if (player.trapVisual?.exists?.()) {
            player.trapVisual.opacity = 1;
        }
    };

    let wasMoving = false;
    let lastRoomId = null;
    let pendingNarrationCheck = false;
    let pendingNarrationPrevRoomId = null;
    let pendingBootNarration = !opts.respawnFromCheckpoint;
    let bootNarrationReadyAt = null;
    let bootNarrationLockActive = false;
    let bootNarrationHasOpened = false;
    let onboardingLockActive = !!opts.tutorialActive;

    onUpdate("player", (p) => {
        if (p.isDead) return;
        setAudioListener(p.pos);
        setAudioWorldPaused(isGameplayPaused());
        if (p.shadow?.exists?.() !== false) {
            p.shadow.opacity = p.isDigging ? 0.14 : 0.28;
        }
        if (p.trapLock > 0) {
            p.trapLock -= dt();
            p.lungeVelocity = vec2(0, 0);
            if (p.trapLock <= 0 && p.trapVisual?.exists?.()) {
                p.trapVisual.opacity = 0;
            }
        }
        const isTrapped = p.trapLock > 0;

        const m = mousePos();
        const escOpen = isEscapeUIOpen();
        const memOpen = isMemoryUIOpen();
        const overMemPanel = isMouseOverMemoryPanel(m);
        const textboxShowing = isTextboxOpen();
        const textboxClickConsumed = isTextboxAdvanceInputConsumed();
        const inTunnel = p.isDigging || isWorldPosInTunnel(p.pos);
        const gameplayPaused = isGameplayPaused();

        const room = getRoomAt(p.pos);
        unloadRegionLockedModules(room?.id ?? null);
        if (room && room.id !== lastRoomId) {
            pendingNarrationPrevRoomId = lastRoomId;
            lastRoomId = room.id;
            pendingNarrationCheck = true;
        }
        // Start-of-run boot dialogue fires ~1s after the first spawn. Respawns
        // skip it so death/reboot doesn't replay onboarding text.
        if (pendingBootNarration) {
            if (bootNarrationReadyAt == null) {
                bootNarrationReadyAt = time() + 1;
                bootNarrationLockActive = true;
            } else if (time() >= bootNarrationReadyAt) {
                pendingBootNarration = false;
                bootNarrationHasOpened = true;
                queueBootNarration();
            }
        }
        if (bootNarrationLockActive && bootNarrationHasOpened && !textboxShowing) {
            bootNarrationLockActive = false;
        }
        // Onboarding stays locked through the tutorial until both core modules
        // are placed AND the storage panel is actually closed.
        if (onboardingLockActive && !isMemoryTutorialLocked() && !isMemoryUIOpen()) {
            onboardingLockActive = false;
        }
        if (pendingNarrationCheck && room && !bootNarrationLockActive && !onboardingLockActive) {
            pendingNarrationCheck = false;
            checkRoomNarration(room.id, pendingNarrationPrevRoomId);
        }
        if (!bootNarrationLockActive && !onboardingLockActive) {
            checkAdSignNarration(Math.floor(p.pos.x / GAME_CONFIG.level.tileWidth));
        }

        const worldPaused = gameplayPaused
            || bootNarrationLockActive
            || onboardingLockActive;
        freezeDogAnims(p, worldPaused || memOpen || escOpen);

        // Can attack if game is not paused, no textbox is up, and mouse is not hovering over the Memory UI panel
        const canAttack = !escOpen && !memOpen && !overMemPanel && !worldPaused && !textboxClickConsumed;

        if (canvas) {
            canvas.style.cursor = (memOpen && overMemPanel) || escOpen || worldPaused ? "default" : "none";
        }
        crosshair.hidden = true;
        const showCrosshair = !escOpen && !memOpen && !worldPaused;
        if (showCrosshair) {
            showCrosshairAt(m);
        } else {
            hideCrosshairOverlay();
        }

        const mouseWorld = toWorld(m);
        p.aimAngle = mouseWorld.sub(p.pos).angle();
        snout.pos = Vec2.fromAngle(p.aimAngle).scale(8);

        if (memOpen) {
            p.lungeVelocity = vec2(0, 0);
            if (wasMoving) {
                wasMoving = false;
                p.dustTimer = 0;
                p.pawTimer = 0;
                saveMemorySystem(p.pos);
            }
            updateDogEyeGlow(p);
            updateVision();
            return;
        }

        if (p.biteCooldown > 0) p.biteCooldown -= dt();
        if (p.biteInvulnerability > 0) p.biteInvulnerability -= dt();
        if (p.eyeActionTimer > 0) p.eyeActionTimer -= dt();
        if (p.barkCooldown > 0) p.barkCooldown -= dt();
        if (p.sniffCooldown > 0) p.sniffCooldown -= dt();

        // Dig Ability (E)
        if (isKeyPressed("e") && isLoaded("action_dig") && !escOpen && !isTrapped && !worldPaused) {
            p.isDigging = !p.isDigging;
            p.dogVisual.color = rgb(255, 255, 255);
            p.dogVisual.opacity = p.isDigging ? 0.6 : 1.0;
            burrowIcon.opacity = p.isDigging ? 1.0 : 0;
        }

        // Sniff Ability (F) — pulse nearby marks, then fade (cooldown)
        const sniffCfg = GAME_CONFIG.sniff;
        if (
            isKeyPressed("f")
            && isLoaded("action_sniff")
            && !escOpen
            && !isTrapped
            && !worldPaused
            && p.sniffCooldown <= 0
        ) {
            p.sniffCooldown = sniffCfg.cooldown ?? 5;
            triggerSniff(p);
            sniffIcon.opacity = 0.95;
            wait(0.35, () => {
                if (sniffIcon.exists()) sniffIcon.opacity = 0;
            });
        }

        updateSniffMarks();

        // Bite Attack (Left Click - Works outside Memory UI panel)
        if (isMousePressed("left") && isLoaded("action_bite") && canAttack && !isTrapped && p.biteCooldown <= 0) {
            shake(SHAKE_BITE);
            playLocal(AUDIO_SOUNDS.bite);
            p.biteCooldown = cfg.biteCooldown;
            p.biteInvulnerability = cfg.biteInvulnerabilityDuration ?? 0.28;
            p.eyeActionTimer = DOG_EYE_ACTION_GLOW_SECONDS;
            p.eyeTrailTimer = 0;
            const lungeDir = Vec2.fromAngle(p.aimAngle);
            p.lungeVelocity = lungeDir.scale(cfg.lungeForce);
            if (Math.abs(lungeDir.x) > 0.1) {
                p.facing = lungeDir.x < 0 ? -1 : 1;
            }
            p.animationLock = 0.28;
            setDogAnimation(p, "attack");

            const biteReach = cfg.biteReach ?? 34;
            const biteRadius = cfg.biteRadius ?? 28;
            const biteStart = p.pos.add(lungeDir.scale(4));
            const biteEnd = p.pos.add(lungeDir.scale(biteReach));
            const getBiteScore = (targetPos, extraRadius = 0) => {
                const segmentDistance = pointDistToSegment(targetPos, biteStart, biteEnd);
                if (segmentDistance > biteRadius + extraRadius) return null;
                return segmentDistance + p.pos.dist(targetPos) * 0.01;
            };

            const biteTargets = [
                ...get("guard_robot", { recursive: true }).map((robot) => ({ type: "robot", obj: robot, extraRadius: 2 })),
                ...get("human", { recursive: true }).map((human) => ({ type: "human", obj: human, extraRadius: 4 })),
            ]
                .map((target) => ({
                    ...target,
                    score: getBiteScore(target.obj.pos, target.extraRadius),
                }))
                .filter((target) => target.score != null)
                .sort((a, b) => a.score - b.score);

            const target = biteTargets[0];
            if (target?.type === "robot") {
                if (target.obj.takeDamage) target.obj.takeDamage(cfg.biteDamage);
                if (target.obj.activate) target.obj.activate();
            } else if (target?.type === "human") {
                triggerHumanAlarm(target.obj, "Attacked!");
            } else {
                const doorTargets = get("cage_door", { recursive: true })
                    .map((door) => ({
                        door,
                        score: getBiteScore(door.pos.add(vec2(16, 16)), 6),
                    }))
                    .filter((doorTarget) => doorTarget.score != null)
                    .sort((a, b) => a.score - b.score);
                if (doorTargets[0]?.door.breakDoor) {
                    doorTargets[0].door.breakDoor();
                }
            }
        }

        // Bark Attack (Right Click - Works outside Memory UI panel)
        if (isMousePressed("right") && isLoaded("action_bark") && canAttack && !inTunnel && !isTrapped && p.barkCooldown <= 0) {
            shake(SHAKE_BARK);
            playBarkLocal();
            p.barkCooldown = cfg.barkCooldown;
            p.eyeActionTimer = Math.max(DOG_EYE_ACTION_GLOW_SECONDS, cfg.barkDuration ?? 0.45);
            p.eyeTrailTimer = 0;
            p.isBarking = true;
            wait(0.3, () => { p.isBarking = false; });

            const maxR = cfg.barkRadius || 70;
            const dur = cfg.barkDuration || 0.45;

            spawnBarkSoundWave(p.pos, maxR, dur);
            attractClosestHumanTo(p.pos);
        }

        if (!isTrapped && !worldPaused && p.lungeVelocity.len() > 10) {
            const lungeMove = movePlayerWithinAlarmLock(p, p.lungeVelocity);
            p.lungeVelocity = lungeMove.hitWall ? vec2(0, 0) : p.lungeVelocity.scale(cfg.lungeDecay);
        } else if (worldPaused) {
            p.lungeVelocity = vec2(0, 0);
        }

        let currentSpeed = 0;
        if (!isTrapped && isLoaded("core_locomotion")) {
            currentSpeed = p.isDigging ? p.baseSpeed * 0.7 : p.baseSpeed;
        }

        let moveX = (isKeyDown("d") ? 1 : 0) - (isKeyDown("a") ? 1 : 0);
        let moveY = (isKeyDown("s") ? 1 : 0) - (isKeyDown("w") ? 1 : 0);
        let dir = vec2(moveX, moveY);
        let isMoving = dir.len() > 0 && currentSpeed > 0 && !worldPaused;
        if (moveX !== 0) {
            p.facing = moveX < 0 ? -1 : 1;
        }

        if (isMoving) {
            dir = dir.unit();
            movePlayerWithinAlarmLock(p, dir.scale(currentSpeed));
            p.dustTimer -= dt();
            if (!escOpen && !memOpen && p.dustTimer <= 0) {
                p.dustTimer = WALK_DUST_INTERVAL;
                spawnWalkDust(p, dir);
            }
            p.pawTimer -= dt();
            if (!escOpen && !memOpen && p.pawTimer <= 0) {
                p.pawTimer = PAW_PRINT_INTERVAL;
                spawnPawPrint(p, dir);
                p.pawStep += 1;
            }
            wasMoving = true;
        } else if (wasMoving) {
            wasMoving = false;
            p.dustTimer = 0;
            p.pawTimer = 0;
            saveMemorySystem(p.pos);
        }

        if (p.animationLock > 0) {
            p.animationLock -= dt();
            p.dogVisual.flipX = p.facing < 0;
        } else {
            setDogAnimation(p, isMoving ? "walk" : "idle");
        }

        const nextCam = getCamPos().lerp(p.pos, 0.2);
        setCamPos(vec2(Math.round(nextCam.x), Math.round(nextCam.y)));
        updateDogEyeGlow(p);
        updateVision();
    });

    return player;
}
